import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import adminService from '../../services/api/adminService';

const ReportsPage = () => {
  const [reportType, setReportType] = useState('booking');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      let data;
      
      if (reportType === 'booking') {
        data = await adminService.getBookingReport({
          reportType: 'Booking',
          startDate: startDate || null,
          endDate: endDate || null,
          groupBy: 'Month',
        });
      } else if (reportType === 'revenue') {
        data = await adminService.getRevenueReport({
          reportType: 'Revenue',
          startDate: startDate || null,
          endDate: endDate || null,
          groupBy: 'Month',
        });
      }
      
      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sample data for demonstration
  const sampleBookingData = [
    { name: 'Jan', Total: 45, Pending: 12, Confirmed: 30, Cancelled: 3 },
    { name: 'Feb', Total: 52, Pending: 15, Confirmed: 35, Cancelled: 2 },
    { name: 'Mar', Total: 48, Pending: 10, Confirmed: 36, Cancelled: 2 },
    { name: 'Apr', Total: 61, Pending: 18, Confirmed: 40, Cancelled: 3 },
    { name: 'May', Total: 55, Pending: 14, Confirmed: 38, Cancelled: 3 },
  ];

  const sampleRevenueData = [
    { name: 'Jan', Revenue: 125000, Bookings: 45 },
    { name: 'Feb', Revenue: 145000, Bookings: 52 },
    { name: 'Mar', Revenue: 135000, Bookings: 48 },
    { name: 'Apr', Revenue: 165000, Bookings: 61 },
    { name: 'May', Revenue: 155000, Bookings: 55 },
  ];

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Reports & Analytics
      </Typography>

      <Paper sx={{ p: 3, mt: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                label="Report Type"
              >
                <MenuItem value="booking">Booking Report</MenuItem>
                <MenuItem value="revenue">Revenue Report</MenuItem>
                <MenuItem value="package">Package Performance</MenuItem>
                <MenuItem value="hotel">Hotel Performance</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGenerateReport}
              disabled={loading}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {reportType === 'booking' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Booking Trends
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={Array.isArray(reportData) ? reportData : (reportData?.data || sampleBookingData)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Total" fill="#8884d8" />
              <Bar dataKey="Pending" fill="#ffc658" />
              <Bar dataKey="Confirmed" fill="#82ca9d" />
              <Bar dataKey="Cancelled" fill="#ff6b6b" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {reportType === 'revenue' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Revenue Trends
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={Array.isArray(reportData) ? reportData : (reportData?.data || sampleRevenueData)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Revenue" stroke="#8884d8" />
              <Line type="monotone" dataKey="Bookings" stroke="#82ca9d" />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Container>
  );
};

export default ReportsPage;
